import { useEffect, useState } from 'react';
import { useCreate, useNotification, useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { useSelect } from '@refinedev/antd';
import { Alert, DatePicker, Form, Modal, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type {
  OrganizationOut,
  OrganizationPublisherCreateIn,
  PublisherListOut,
} from '@/api/generated/schemas';
import {
  isDisabledLinkDate,
  type OrganizationPublisherLinkRow,
} from '@/components/tenancy/organizationPublisherLinks';
import { PUBLISHER_TREE_LIMIT } from '@/components/user_catalog/publishers/publisherTree';
import { extractBackendMessage } from '@/utils/apiError';
import { formatDate } from '@/utils/format';

type AddPublisherValues = {
  publisher_id: number;
  valid_from: Dayjs;
};

interface AddPublisherModalProps {
  open: boolean;
  onClose: () => void;
  record: OrganizationOut;
  /** The parent's link to add an imprint under; `null` adds a parent. */
  parentLink: OrganizationPublisherLinkRow | null;
  parentName?: string;
}

export const AddPublisherModal = ({
  open,
  onClose,
  record,
  parentLink,
  parentName,
}: AddPublisherModalProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const { mutateAsync } = useCreate();
  const [form] = Form.useForm<AddPublisherValues>();
  const [submitting, setSubmitting] = useState(false);

  const tk = (suffix: string, options?: Record<string, unknown>) =>
    t(`tenancy.organizations.publishers.add.${suffix}`, options);

  // Parents join on their own; an imprint only under its parent, so it is
  // offered from the parent's row alone. Any publisher qualifies — a move
  // from another organization is a link here too.
  const filters: CrudFilter[] = parentLink
    ? [{ field: 'parent_id', operator: 'eq', value: parentLink.publisher_id }]
    : [{ field: 'parent_id', operator: 'null', value: true }];
  const { selectProps } = useSelect<PublisherListOut>({
    resource: 'user_catalog.publishers',
    optionLabel: 'name_with_role',
    optionValue: 'id',
    // Permanent filter — useSelect appends the search filters to it.
    filters,
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
    pagination: { pageSize: PUBLISHER_TREE_LIMIT, mode: 'server' },
    queryOptions: { enabled: open },
  });

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setSubmitting(false);
    }
  }, [open, form]);

  const handleSubmit = async () => {
    let values: AddPublisherValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const payload: OrganizationPublisherCreateIn = {
      organization_id: record.id,
      publisher_id: values.publisher_id,
      valid_from: values.valid_from.format('YYYY-MM-DD'),
    };

    setSubmitting(true);
    try {
      await mutateAsync({
        resource: 'tenancy.organization_publishers',
        values: payload,
        successNotification: false,
        errorNotification: false,
      });
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk('success'),
      });
      onClose();
    } catch (error) {
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description: extractBackendMessage(error) ?? tk('error_fallback'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const parent = parentName ?? parentLink?.publisher_id;
  const info = !parentLink
    ? tk('parent_info')
    : parentLink.next
      ? tk('imprint_period_range', {
          parent,
          from: formatDate(parentLink.valid_from),
          // The next link starts elsewhere; the last day here is before it.
          to: formatDate(
            dayjs(parentLink.next.valid_from)
              .subtract(1, 'day')
              .format('YYYY-MM-DD')
          ),
        })
      : tk('imprint_period_from', {
          parent,
          from: formatDate(parentLink.valid_from),
        });

  return (
    <Modal
      open={open}
      title={parentLink ? tk('imprint_title', { parent }) : tk('title')}
      okText={tk('submit')}
      okButtonProps={{ loading: submitting }}
      cancelText={t('common.cancel')}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={!submitting}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
      width={520}
    >
      <Alert type="info" showIcon message={info} style={{ marginBottom: 16 }} />
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        disabled={submitting}
      >
        <Form.Item
          name="publisher_id"
          label={t('tenancy.organizations.fields.publisher')}
          rules={[{ required: true }]}
        >
          <Select
            {...selectProps}
            showSearch
            filterOption={false}
            placeholder={tk(
              parentLink ? 'imprint_placeholder' : 'parent_placeholder'
            )}
          />
        </Form.Item>

        <Form.Item
          name="valid_from"
          label={t('tenancy.organizations.fields.valid_from')}
          extra={tk('valid_from_hint')}
          rules={[{ required: true }]}
        >
          {/* Links start on a day after today — the backend validates
              contracts with the same rule — and an imprint only while its
              parent is here. */}
          <DatePicker
            format="DD.MM.YYYY"
            style={{ width: '100%' }}
            disabledDate={(date) =>
              isDisabledLinkDate(date, parentLink ?? undefined)
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
